import type { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';

import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { LIST_VIEW_MESSAGES } from '@/shared/config/listViewMessages';
import { listViewErrorDescriptor } from '@/shared/lib/listViewMessages';

type NextPageQuery = {
  isFetchNextPageError: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
};

function RetryAlert({
  message,
  onRetry,
  className,
}: {
  message: MessageDescriptor;
  onRetry: () => void;
  className?: string;
}) {
  const { i18n } = useLingui();
  return (
    <Alert variant="destructive" className={className}>
      <AlertTitle>{i18n._(message)}</AlertTitle>
      <AlertDescription>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          {i18n._(LIST_VIEW_MESSAGES.retry)}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function ListBlocked() {
  const { i18n } = useLingui();
  return (
    <Alert variant="destructive">
      <AlertTitle>{i18n._(LIST_VIEW_MESSAGES.blocked)}</AlertTitle>
    </Alert>
  );
}

function ListFailed({ error, retry }: { error: unknown; retry: () => void }) {
  return <RetryAlert message={listViewErrorDescriptor(error)} onRetry={retry} />;
}

function ListNextPageFooter({
  query,
}: {
  query: NextPageQuery;
}) {
  const { i18n } = useLingui();
  if (query.isFetchNextPageError) {
    return (
      <RetryAlert
        message={LIST_VIEW_MESSAGES.nextPageError}
        onRetry={() => void query.fetchNextPage()}
        className="mt-4"
      />
    );
  }

  if (!query.isFetchingNextPage) return null;

  return (
    <p className="flex items-center justify-center gap-2 py-3 text-muted-foreground text-sm">
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      {i18n._(LIST_VIEW_MESSAGES.loadingMore)}
    </p>
  );
}

export { ListBlocked, ListFailed, ListNextPageFooter };
